import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { mediaUrl } from "@/context/SiteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { COUNTIES } from "@/lib/data";

const IMAGE_FIELDS = [
  { key: "hero_image_id", label: "Homepage Hero Image", hint: "Large image on the homepage hero. Best: a caregiver assisting a passenger by a wheelchair-accessible van. Wide/landscape works best." },
  { key: "nemt_image_id", label: "Transportation Card — Non-Emergency Medical", hint: "Homepage & Services photo card. A driver helping a passenger into a vehicle." },
  { key: "wheelchair_image_id", label: "Transportation Card — Wheelchair Accessible", hint: "Staff assisting a wheelchair passenger boarding an accessible van." },
  { key: "ambulatory_image_id", label: "Transportation Card — Ambulatory", hint: "Driver assisting an ambulatory passenger entering a vehicle." },
  { key: "odp_video_image_id", label: "ODP Video Thumbnail", hint: "Large 'See Care Beyond the Ride' visual behind the play button (homepage & ODP page)." },
  { key: "careers_hero_image_id", label: "Careers Page Hero", hint: "Recruitment photo on the Careers page hero — a uniformed CareDeserved driver with a transportation van works best. Wide/landscape." },
  { key: "odp_hab_image_id", label: "ODP — Home & Community Habilitation", hint: "Someone building skills: cooking, art, shopping, or a community activity." },
  { key: "odp_companion_image_id", label: "ODP — Companion Services", hint: "A support professional and individual enjoying a community activity together." },
  { key: "odp_nursing_image_id", label: "ODP — Registered Nursing", hint: "A nurse compassionately supporting an individual in a home setting." },
  { key: "facilities_image_id", label: "Facilities Section Image", hint: "Shown in the 'For Facilities & Partners' section (homepage + facilities page)." },
  { key: "care_image_id", label: "About / Compassion Image", hint: "Shown on the About page." },
];

function ImageUploader({ field, currentId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.file_id);
      toast.success("Image uploaded & optimized");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="h-28 w-full overflow-hidden rounded-xl bg-slate-100 sm:w-48 shrink-0">
          {currentId ? (
            <img src={mediaUrl(currentId)} alt="Current" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-400"><ImageIcon size={28} weight="duotone" /></div>
          )}
        </div>
        <div className="flex-1">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-blue2">
            <UploadSimple size={18} /> {uploading ? "Uploading…" : currentId ? "Replace Image" : "Upload Image"}
            <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} data-testid={`upload-${field}`} className="hidden" />
          </label>
          {currentId && (
            <button onClick={() => onUploaded("")} data-testid={`remove-${field}`}
              className="ml-3 text-sm font-medium text-slate-500 hover:text-rose-600">Use default</button>
          )}
          <p className="mt-2 text-xs text-slate-400">JPG, PNG or WEBP. Automatically optimized and resized for fast, distortion-free display on all devices.</p>
        </div>
      </div>
    </div>
  );
}

export default function SiteContent() {
  const [form, setForm] = useState(null);
  const [testing, setTesting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/send-test-email");
      if (data.daily_sent && data.weekly_sent) {
        toast.success(`Test emails sent to ${data.to}`);
      } else {
        toast.warning(`Sent with issues — Daily: ${data.daily_sent ? "ok" : "failed"}, Weekly: ${data.weekly_sent ? "ok" : "failed"}`);
      }
    } catch {
      toast.error("Could not send test emails. Please try again.");
    } finally { setTesting(false); }
  };

  useEffect(() => { api.get("/admin/content").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const save = async (payload) => {
    const body = payload || form;
    try {
      await api.put("/admin/content", body);
      toast.success("Content saved");
    } catch { toast.error("Save failed"); }
  };

  // When an image changes, persist immediately so the public site updates right away.
  const setImage = (key) => async (fileId) => {
    const next = { ...form, [key]: fileId };
    setForm(next);
    await save(next);
  };

  if (!form) return <p className="text-slate-400">Loading…</p>;

  return (
    <div data-testid="admin-content" className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Site Content & Homepage</h1>
      <p className="mt-1 text-sm text-slate-500">Update contact details, office hours, homepage copy, and images — no code required.</p>

      {/* Homepage Images */}
      <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Website Images</h2>
        <p className="-mt-2 text-sm text-slate-500">Replace any photo with your own CareDeserved imagery. Uploads are optimized automatically and appear across the site immediately. All images are also managed in the Media Library.</p>
        {IMAGE_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-sm font-medium text-slate-700">{f.label}</Label>
            <p className="mb-2 text-xs text-slate-400">{f.hint}</p>
            <ImageUploader field={f.key} currentId={form[f.key]} onUploaded={setImage(f.key)} />
          </div>
        ))}
      </div>

      {/* Video links */}
      <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Story Videos</h2>
        <p className="-mt-2 text-sm text-slate-500">
          Paste a direct video URL (e.g. an MP4 link). When set, the "See how we care" (homepage hero) and "See Care Beyond the Ride" (ODP) play buttons will open your real video. Leave blank to show the branded "coming soon" message.
        </p>
        <div className="space-y-1.5"><Label>Homepage Hero Video URL</Label><Input data-testid="c-hero-video" placeholder="https://…/caredeserved-hero.mp4" value={form.hero_video_url || ""} onChange={set("hero_video_url")} /></div>
        <div className="space-y-1.5"><Label>ODP "Care Beyond the Ride" Video URL</Label><Input data-testid="c-odp-video" placeholder="https://…/caredeserved-odp.mp4" value={form.odp_video_url || ""} onChange={set("odp_video_url")} /></div>
        <Button onClick={() => save()} data-testid="c-video-save" className="rounded-full bg-brand-purple px-8 text-white hover:bg-brand-purple2">Save Video Links</Button>
      </div>

      {/* Social media */}
      <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Social Media</h2>
        <p className="-mt-2 text-sm text-slate-500">Add your official CareDeserved profile URLs (must start with https://). Leave any blank to automatically hide that icon in the footer — no dead links.</p>
        {[["social_facebook", "Facebook"], ["social_instagram", "Instagram"], ["social_linkedin", "LinkedIn"], ["social_x", "X (Twitter)"], ["social_youtube", "YouTube"]].map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label>{l}</Label><Input data-testid={`c-${k}`} placeholder="https://…" value={form[k] || ""} onChange={set(k)} /></div>
        ))}
        <Button onClick={() => save()} data-testid="c-social-save" className="rounded-full bg-brand-purple px-8 text-white hover:bg-brand-purple2">Save Social Links</Button>
      </div>

      {/* WhatsApp */}
      <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">WhatsApp Contact</h2>
            <p className="mt-1 text-sm text-slate-500">Shown in the footer, on the Contact page, and as a floating button. Turn off to hide everywhere.</p>
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-2">
            <input type="checkbox" data-testid="c-whatsapp-enabled"
              checked={form.whatsapp_enabled !== false}
              onChange={(e) => setForm((f) => ({ ...f, whatsapp_enabled: e.target.checked }))}
              className="h-5 w-5 rounded border-slate-300 accent-brand-purple" />
            <span className="text-sm font-medium text-slate-700">{form.whatsapp_enabled !== false ? "Visible" : "Hidden"}</span>
          </label>
        </div>
        <div className="space-y-1.5"><Label>WhatsApp Number</Label><Input data-testid="c-whatsapp-number" placeholder="+1 484-877-5855" value={form.whatsapp_number || ""} onChange={set("whatsapp_number")} /></div>
        <div className="space-y-1.5"><Label>WhatsApp Username</Label><Input data-testid="c-whatsapp-username" placeholder="@caredeserved" value={form.whatsapp_username || ""} onChange={set("whatsapp_username")} /></div>
        <div className="space-y-1.5"><Label>Pre-filled Message</Label><Textarea data-testid="c-whatsapp-prefill" className="min-h-[80px]" placeholder="Hello CareDeserved, I'm contacting you through your website…" value={form.whatsapp_prefill || ""} onChange={set("whatsapp_prefill")} /></div>
        <Button onClick={() => save()} data-testid="c-whatsapp-save" className="rounded-full bg-brand-purple px-8 text-white hover:bg-brand-purple2">Save WhatsApp Settings</Button>
      </div>

      {/* Driver Recruitment */}
      <div className="mt-6 space-y-5 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Driver Recruitment Banner</h2>
            <p className="mt-1 text-sm text-slate-500">A location-aware "Drivers Wanted" banner shown on the homepage and county pages. Turn off to hide it everywhere.</p>
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-2">
            <input type="checkbox" data-testid="c-recruiting-enabled"
              checked={form.recruiting_enabled !== false}
              onChange={(e) => setForm((f) => ({ ...f, recruiting_enabled: e.target.checked }))}
              className="h-5 w-5 rounded border-slate-300 accent-brand-purple" />
            <span className="text-sm font-medium text-slate-700">{form.recruiting_enabled !== false ? "Visible" : "Hidden"}</span>
          </label>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" data-testid="c-recruiting-fulltime"
              checked={form.recruiting_fulltime !== false}
              onChange={(e) => setForm((f) => ({ ...f, recruiting_fulltime: e.target.checked }))}
              className="h-5 w-5 rounded border-slate-300 accent-brand-purple" />
            <span className="text-sm font-medium text-slate-700">Full-Time positions</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" data-testid="c-recruiting-parttime"
              checked={form.recruiting_parttime !== false}
              onChange={(e) => setForm((f) => ({ ...f, recruiting_parttime: e.target.checked }))}
              className="h-5 w-5 rounded border-slate-300 accent-brand-purple" />
            <span className="text-sm font-medium text-slate-700">Part-Time positions</span>
          </label>
        </div>

        <div>
          <Label>Counties currently hiring</Label>
          <p className="mb-2 text-xs text-slate-400">Only checked counties show a county-specific hiring message. Visitors elsewhere see the generic banner.</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {COUNTIES.map((c) => {
              const list = Array.isArray(form.recruiting_counties) ? form.recruiting_counties : [];
              const on = list.includes(c);
              return (
                <label key={c} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                  <input type="checkbox" data-testid={`c-recruiting-county-${c.toLowerCase()}`}
                    checked={on}
                    onChange={() => setForm((f) => {
                      const cur = Array.isArray(f.recruiting_counties) ? f.recruiting_counties : [];
                      return { ...f, recruiting_counties: on ? cur.filter((x) => x !== c) : [...cur, c] };
                    })}
                    className="h-4 w-4 rounded border-slate-300 accent-brand-purple" />
                  <span className="text-sm text-slate-700">{c} County</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-1.5"><Label>Headline</Label><Input data-testid="c-recruiting-headline" placeholder="Drivers Wanted" value={form.recruiting_headline || ""} onChange={set("recruiting_headline")} /><p className="text-xs text-slate-400">On county pages, "— {`{County}`} County" is added automatically.</p></div>
        <div className="space-y-1.5"><Label>Recruitment Description</Label><Textarea data-testid="c-recruiting-description" className="min-h-[110px]" value={form.recruiting_description || ""} onChange={set("recruiting_description")} /></div>
        <div className="space-y-1.5"><Label>Pay Information (optional)</Label><Input data-testid="c-recruiting-pay" placeholder="Leave blank to show no compensation" value={form.recruiting_pay || ""} onChange={set("recruiting_pay")} /><p className="text-xs text-slate-400">Only shown if you enter something here.</p></div>
        <div className="space-y-1.5"><Label>Application Button Text</Label><Input data-testid="c-recruiting-button" placeholder="Apply Today" value={form.recruiting_button_text || ""} onChange={set("recruiting_button_text")} /></div>

        <div>
          <Label>Banner Image</Label>
          <p className="mb-2 text-xs text-slate-400">Shown beside the recruitment text on larger screens. Leave as default to use our branded driver photo.</p>
          <ImageUploader field="recruiting_image_id" currentId={form.recruiting_image_id} onUploaded={setImage("recruiting_image_id")} />
        </div>

        <Button onClick={() => save()} data-testid="c-recruiting-save" className="rounded-full bg-brand-purple px-8 text-white hover:bg-brand-purple2">Save Recruitment Settings</Button>
      </div>

      {/* Careers page */}
      <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Careers Page</h2>
        <p className="-mt-2 text-sm text-slate-500">Edit the "What We're Looking For" list shown on the Careers page. One item per line. The hero photo is managed above under "Careers Page Hero".</p>
        <div className="space-y-1.5">
          <Label>What We're Looking For (one per line)</Label>
          <Textarea data-testid="c-careers-requirements" className="min-h-[150px]" value={form.careers_requirements || ""} onChange={set("careers_requirements")} />
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-5">
          <div>
            <Label>Homepage Recruitment Ticker</Label>
            <p className="mt-0.5 text-xs text-slate-400">Slim rolling bar below the homepage hero that links to the Careers page.</p>
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-2">
            <input type="checkbox" data-testid="c-ticker-enabled"
              checked={form.recruit_ticker_enabled !== false}
              onChange={(e) => setForm((f) => ({ ...f, recruit_ticker_enabled: e.target.checked }))}
              className="h-5 w-5 rounded border-slate-300 accent-brand-purple" />
            <span className="text-sm font-medium text-slate-700">{form.recruit_ticker_enabled !== false ? "Visible" : "Hidden"}</span>
          </label>
        </div>
        <div className="space-y-1.5">
          <Label>Ticker Message</Label>
          <Input data-testid="c-ticker-text" placeholder="Want to Drive for CareDeserved? …" value={form.recruit_ticker_text || ""} onChange={set("recruit_ticker_text")} />
        </div>
        <Button onClick={() => save()} data-testid="c-careers-save" className="rounded-full bg-brand-purple px-8 text-white hover:bg-brand-purple2">Save Careers Content</Button>
      </div>

      {/* Text content */}
      <div className="mt-6 space-y-5 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Text & Contact Info</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="c-phone" value={form.phone || ""} onChange={set("phone")} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="c-email" value={form.email || ""} onChange={set("email")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Website</Label><Input data-testid="c-website" value={form.website || ""} onChange={set("website")} /></div>
        </div>
        <div className="space-y-1.5"><Label>Office Hours</Label><Textarea data-testid="c-hours" className="min-h-[100px]" value={form.office_hours || ""} onChange={set("office_hours")} /></div>
        <div className="space-y-1.5"><Label>Hero Headline</Label><Input data-testid="c-headline" value={form.hero_headline || ""} onChange={set("hero_headline")} /></div>
        <p className="-mt-3 text-xs text-slate-400">Tip: separate the headline into phrases with periods (e.g. "Reliable Rides. Compassionate Care. Every Time.") — each phrase shows on its own line.</p>
        <div className="space-y-1.5"><Label>Hero Subtext</Label><Textarea data-testid="c-subtext" className="min-h-[120px]" value={form.hero_subtext || ""} onChange={set("hero_subtext")} /></div>
        <div className="space-y-1.5"><Label>Service Areas Heading</Label><Input data-testid="c-sa-heading" value={form.service_areas_heading || ""} onChange={set("service_areas_heading")} /></div>
        <div className="space-y-1.5"><Label>Service Areas Text</Label><Textarea data-testid="c-sa-text" className="min-h-[100px]" value={form.service_areas_text || ""} onChange={set("service_areas_text")} /></div>
        <Button onClick={() => save()} data-testid="c-save" className="rounded-full bg-brand-purple px-8 text-white hover:bg-brand-purple2">Save Changes</Button>
      </div>

      {/* Email reminders */}
      <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Email Reminders</h2>
        <p className="-mt-2 text-sm text-slate-500">
          Set the time the daily follow-up reminder email is sent to {form.email || "your office inbox"}.
          The weekly management summary is sent every Monday at this same time. All times are U.S. Eastern (auto-adjusts for daylight saving).
        </p>
        <div className="max-w-xs space-y-1.5">
          <Label>Daily reminder time (Eastern)</Label>
          <Input type="time" data-testid="c-digest-time" value={form.digest_send_time || "07:00"} onChange={set("digest_send_time")} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => save()} data-testid="c-digest-save" className="rounded-full bg-brand-teal px-8 text-white hover:bg-brand-teal2">Save Reminder Time</Button>
          <Button onClick={sendTest} disabled={testing} data-testid="c-send-test" variant="outline" className="rounded-full border-brand-blue px-8 font-semibold text-brand-blue hover:bg-brand-blue hover:text-white">
            {testing ? "Sending…" : "Send Test Email"}
          </Button>
        </div>
        <p className="text-xs text-slate-400">
          "Send Test Email" immediately emails a clearly-labeled <strong>TEST</strong> copy of both the Daily Follow-Up Reminder and the Weekly Management Summary to {form.email || "your office inbox"}, so you can preview them. Test emails do not affect the scheduled reports.
        </p>
      </div>
    </div>
  );
}
